import { ComponentProps, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSearch } from '@treams/treams-hooks/useSearch';
import { UserStatus } from '@treams/treams-network';

import { Select } from './Select';

type SelectProps = ComponentProps<typeof Select>;
const BASE_SELECT_PROPS: Partial<SelectProps> = {
	autofocusMenu: true,
	disableFilter: true,
};

type SearchResultUser = ReturnType<typeof useSearch>['results'][number];

type UserSearchOptions = {
	excluded?: string[];
	includeViewer?: boolean;
	suggestGroups?: {
		title: string;
		userIDs: Set<string>;
	}[];
	sortBy?: 'fullName';
	selectedUserID?: string;
	setSelected: (userID: string, user: SearchResultUser) => void;
};

function useUserSearchSelectProps(props: UserSearchOptions) {
	const { t } = useTranslation();
	const [inputValue, setInputValue] = useState<string>('');
	const { results, loading } = useSearch(inputValue, {
		excludeViewer: !props.includeViewer,
		status: [UserStatus.Active],
	});

	const filteredResults = useMemo(() => {
		return results.filter((user) => !props.excluded?.includes(user.id));
	}, [results, props.excluded]);

	const sortedResults = useMemo(() => {
		switch (props.sortBy) {
			case 'fullName':
				return filteredResults.sort((a, b) => {
					return a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName);
				});
			default:
				return filteredResults;
		}
	}, [filteredResults, props.sortBy]);

	const options = useMemo(() => {
		if (sortedResults.length === 0 && !loading) {
			return [
				{
					title: t('users.collaborators.selector.noResults'),
					value: '',
					disabled: true,
				},
			];
		}

		const flatOptions = sortedResults.map((user) => ({
			title: `${user.firstName} ${user.lastName}`,
			value: user.id,
		}));

		const groupedOptions = (props.suggestGroups ?? [])
			.map((group) => ({
				title: group.title,
				options: flatOptions.filter((user) => group.userIDs.has(user.value)),
			}))
			.filter((group) => group.options.length > 0);

		if (groupedOptions.length === 0) {
			return flatOptions;
		}

		const grouplessOptions = flatOptions.filter(
			(user) => !props.suggestGroups?.some((group) => group.userIDs.has(user.value)),
		);

		return [...groupedOptions, { title: t('common.others'), options: grouplessOptions }];
	}, [sortedResults, loading, t, props.suggestGroups]);

	return {
		onQueryChange: setInputValue,
		options: loading ? undefined : options,
		selectedValue: props.selectedUserID,
		onChange: (value) => {
			const searchResult = !!value && results.find((user) => user.id === value);

			if (searchResult) {
				props.setSelected(searchResult.id, searchResult);
			}
		},
	} satisfies SelectProps;
}

type UserSelectProps = UserSearchOptions &
	Omit<SelectProps, 'onQueryChange' | 'options' | 'onChange' | 'selectedValue'>;

export function UserSelect({
	excluded,
	includeViewer,
	sortBy,
	selectedUserID,
	setSelected,
	suggestGroups,
	...selectMenuProps
}: UserSelectProps) {
	const { t } = useTranslation();
	const searchSelectProps = useUserSearchSelectProps({
		excluded,
		includeViewer,
		sortBy,
		selectedUserID,
		setSelected,
		suggestGroups,
	});

	return (
		<Select
			placeholder={t('users.collaborators.selector.add.buttonTitle')}
			{...BASE_SELECT_PROPS}
			{...searchSelectProps}
			{...selectMenuProps}
		/>
	);
}

function UserSelectMenu({
	excluded,
	includeViewer,
	sortBy,
	selectedUserID,
	setSelected,
	suggestGroups,
	...selectMenuProps
}: UserSelectProps) {
	const { autofocusMenu, ...baseSelectMenuProps } = BASE_SELECT_PROPS;
	const searchSelectProps = useUserSearchSelectProps({
		excluded,
		includeViewer,
		sortBy,
		selectedUserID,
		setSelected,
		suggestGroups,
	});

	return (
		<Select.Menu
			{...selectMenuProps}
			{...baseSelectMenuProps}
			autofocus={autofocusMenu}
			{...searchSelectProps}
			{...selectMenuProps}
		/>
	);
}

UserSelect.Menu = UserSelectMenu;
